import type { EventEnvelope } from '../events.js';
import type { VentureId } from './common.js';
import type { CaseStepStatus } from './sop.js';

/**
 * Versioned HR domain events (People Engine — ADR 0032 discipline, ADR 0052).
 *
 * Same rules as the core event map: versioned names (`hr.case.opened.v1`),
 * every payload wrapped in the {@link EventEnvelope} (correlation id + injected
 * clock), emitted for domain-significant outcomes only — never ephemeral UI
 * state.
 */

/** `hr.case.opened.v1` — a playbook execution started. */
export interface HrCaseOpenedV1 extends EventEnvelope {
  readonly caseId: string;
  readonly ventureId: VentureId;
  readonly playbookKey: string;
  readonly playbookVersion: number;
  readonly employeeId?: string;
}

/** `hr.case.step.recorded.v1` — an audit-trail row was appended to a case. */
export interface HrCaseStepRecordedV1 extends EventEnvelope {
  readonly caseId: string;
  readonly ventureId: VentureId;
  readonly stepId: string;
  readonly status: CaseStepStatus;
}

/** `hr.case.closed.v1` — a case reached a terminal state. */
export interface HrCaseClosedV1 extends EventEnvelope {
  readonly caseId: string;
  readonly ventureId: VentureId;
  readonly outcome: 'completed' | 'rejected' | 'cancelled';
}

/** `hr.employee.added.v1` — a new employee record entered the registry. */
export interface HrEmployeeAddedV1 extends EventEnvelope {
  readonly employeeId: string;
  readonly ventureId: VentureId;
}

/** `hr.leave.balance.updated.v1` — a leave decision changed an entitlement balance. */
export interface HrLeaveBalanceUpdatedV1 extends EventEnvelope {
  readonly employeeId: string;
  readonly ventureId: VentureId;
  readonly balanceDays: number;
}

/** Central versioned-name → payload map for the People Engine's event bus. */
export interface HrEventMap {
  'hr.case.opened.v1': HrCaseOpenedV1;
  'hr.case.step.recorded.v1': HrCaseStepRecordedV1;
  'hr.case.closed.v1': HrCaseClosedV1;
  'hr.employee.added.v1': HrEmployeeAddedV1;
  'hr.leave.balance.updated.v1': HrLeaveBalanceUpdatedV1;
}

/** Canonical HR event names (same pattern as the core `EVENTS` map). */
export const HR_EVENTS = {
  caseOpened: 'hr.case.opened.v1',
  caseStepRecorded: 'hr.case.step.recorded.v1',
  caseClosed: 'hr.case.closed.v1',
  employeeAdded: 'hr.employee.added.v1',
  leaveBalanceUpdated: 'hr.leave.balance.updated.v1',
} as const satisfies Record<string, keyof HrEventMap>;
